import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import DanceStyles from "@/pages/DanceStyles";
import DanceStyleDetail from "@/pages/DanceStyleDetail";
import Watch from "@/pages/Watch";
import Festival from "@/pages/Festival";
import OrganizerLanding from "@/pages/OrganizerLanding";
import AdminLayout, { AdminOnly } from "@/pages/admin/AdminLayout";
import OrganizerOnboarding from "@/pages/admin/OrganizerOnboarding";
import AdminVideos from "@/pages/admin/AdminVideos";
import AdminVideoDetail from "@/pages/admin/AdminVideoDetail";
import AdminFestivals from "@/pages/admin/AdminFestivals";
import AdminFestivalDetail from "@/pages/admin/AdminFestivalDetail";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminDanceStyles from "@/pages/admin/AdminDanceStyles";
import AdminDesign from "@/pages/admin/AdminDesign";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminSales from "@/pages/admin/AdminSales";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import PaymentSuccess, { PaymentCancel } from "@/pages/Payment";
import AdminHome from "@/pages/admin/AdminHome";
import AdminAccount from "@/pages/admin/AdminAccount";
import SearchResults from "@/pages/SearchResults";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PublicLayout = () => (
  <div className="min-h-screen">
    <Header />
    <main className="mx-auto max-w-[1280px] 2xl:max-w-[1560px] 3xl:max-w-[1840px] 4xl:max-w-[2280px] px-4 sm:px-6 lg:px-10 4xl:px-16 pb-24">
      <Outlet />
    </main>
  </div>
);

const RequireAdmin = ({ children }) => {
  const { user } = useAuth();
  if (user === undefined) return <div className="p-10 text-sm text-[#6B7280]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors toastOptions={{ style: { fontFamily: "Space Grotesk, sans-serif" } }} />
        <ScrollToTop />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Home loginOpen />} />
            <Route path="/performances" element={<Browse tag="Performance" title="Performances" />} />
            <Route path="/competitions" element={<Browse tag="Competition" title="Competitions" />} />
            <Route path="/festivals" element={<Browse tag="Festival" title="Festivals" />} />
            <Route path="/festivals/:id" element={<Festival />} />
            <Route path="/styles" element={<DanceStyles />} />
            <Route path="/styles/:slug" element={<DanceStyleDetail />} />
            <Route path="/watch/:id" element={<Watch />} />
            <Route path="/search" element={<SearchResults />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
          </Route>
          <Route path="/organizer" element={<OrganizerLanding />} />
          <Route
            path="/admin/onboarding"
            element={
              <RequireAdmin>
                <OrganizerOnboarding />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            }
          >
            <Route index element={<AdminHome />} />
            <Route path="videos" element={<AdminVideos />} />
            <Route path="videos/:id" element={<AdminVideoDetail />} />
            <Route path="festivals" element={<AdminFestivals />} />
            <Route path="festivals/:id" element={<AdminFestivalDetail />} />
            <Route path="categories" element={<AdminOnly><AdminCategories /></AdminOnly>} />
            <Route path="dance-styles" element={<AdminOnly><AdminDanceStyles /></AdminOnly>} />
            <Route path="design" element={<AdminOnly><AdminDesign /></AdminOnly>} />
            <Route path="featured" element={<Navigate to="/admin/design" replace />} />
            <Route path="sales" element={<AdminSales />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="customers/:email" element={<AdminCustomerDetail />} />
            <Route path="payouts" element={<AdminPayouts />} />
            <Route path="users" element={<AdminOnly><AdminUsers /></AdminOnly>} />
            <Route path="users/:id" element={<AdminOnly><AdminUserDetail /></AdminOnly>} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="account" element={<AdminAccount />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
